import { api } from '../context/AuthContext';

const urlB64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const pushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (e) {
    console.error('SW registration failed', e);
    return null;
  }
};

const getServerKey = async () => {
  try {
    const data = await api('/api/push/vapid-public-key');
    if (data?.public_key) return data.public_key;
  } catch { /* fall through */ }
  return process.env.REACT_APP_VAPID_PUBLIC_KEY || null;
};

const sameKey = (sub, key) => {
  const a = sub.options?.applicationServerKey;
  if (!a) return true;
  const x = new Uint8Array(a);
  const y = urlB64ToUint8Array(key);
  return x.length === y.length && x.every((v, i) => v === y[i]);
};

// Subscribe with the SERVER's key (the one it signs pushes with); replace stale subscriptions.
const subscribeWithServerKey = async (reg) => {
  const key = await getServerKey();
  if (!key) return { ok: false, reason: 'no-vapid' };
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub, key)) { await sub.unsubscribe(); sub = null; }
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(key) });
  const json = sub.toJSON();
  await api('/api/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys, user_agent: navigator.userAgent }),
  });
  return { ok: true };
};

export const enablePush = async () => {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: perm };
  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: 'no-sw' };
  return subscribeWithServerKey(reg);
};

// Silent re-sync on app open (permission already granted): keeps this device linked to the signed-in user.
export const syncPush = async () => {
  if (!pushSupported() || Notification.permission !== 'granted') return;
  try {
    const reg = await registerServiceWorker();
    if (reg) await subscribeWithServerKey(reg);
  } catch (e) { console.error('push sync failed', e); }
};

export const disablePush = async () => {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    const json = sub.toJSON();
    try {
      await api('/api/push/unsubscribe', {
        method: 'POST',
        body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys }),
      });
    } catch { /* ignore */ }
    await sub.unsubscribe();
  }
};

// iOS Safari only supports web-push when the site is installed as a PWA (iOS ≥ 16.4).
export const isIOSSafariPWANeeded = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  return isIOS && !isStandalone;
};
